import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Send, Instagram, Shield, Map, Users, Trophy, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { NearbyGamesSection } from "../components/NearbyGamesSection";

const API = process.env.REACT_APP_BACKEND_URL || "";

const GAME_LOGOS = [
  { src: "/game-logos/magic.png", name: "Magic: The Gathering" },
  { src: "/game-logos/pokemon.png", name: "Pokemon TCG" },
  { src: "/game-logos/yugioh.png", name: "Yu-Gi-Oh!" },
  { src: "/game-logos/lorcana.png", name: "Disney Lorcana" },
  { src: "/game-logos/altered.png", name: "Altered" },
  { src: "/game-logos/dragonball.png", name: "Dragon Ball Super" },
  { src: "/game-logos/flesh-and-blood.png", name: "Flesh & Blood" },
  { src: "/game-logos/onepiece.png", name: "One Piece TCG" },
  { src: "/game-logos/starwars.png", name: "Star Wars" },
  { src: "/game-logos/sorcery.png", name: "Sorcery" },
  { src: "/game-logos/riftbound.png", name: "Riftbound" },
];

export default function LandingPage() {
  const { isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [contactSent, setContactSent] = useState(false);
  const [currentLogo, setCurrentLogo] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setCurrentLogo(c => (c + 1) % GAME_LOGOS.length), 2500);
    return () => clearInterval(t);
  }, []);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    await fetch(`${API}/api/newsletter/subscribe`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) });
    setEmailSent(true);
    setEmail("");
  };

  const handleContact = async (e) => {
    e.preventDefault();
    await fetch(`${API}/api/contact`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(contactForm) });
    setContactSent(true);
    setContactForm({ name: "", email: "", message: "" });
  };

  return (
    <div className="min-h-screen bg-[#1a1f36] text-white overflow-x-hidden" data-testid="landing-page">

      {/* === NAV === */}
      <nav className="fixed top-0 w-full z-50 bg-[#1a1f36]/90 backdrop-blur-md border-b border-white/5" style={{ paddingTop: 'env(safe-area-inset-top, 0)' }}>
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-complet-arcanapp.png" alt="ArcanApp" className="h-8" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            {isAuthenticated ? (
              <Link to="/home" className="bg-[#FF6B35] hover:bg-[#e55a25] text-white text-xs sm:text-sm font-semibold px-3 sm:px-5 py-2 rounded-full transition-colors" data-testid="nav-go-app">
                {user?.pseudo || 'Mon espace'}
              </Link>
            ) : (
              <Link to="/login" className="bg-[#FF6B35] hover:bg-[#e55a25] text-white text-xs sm:text-sm font-semibold px-3 sm:px-5 py-2 rounded-full transition-colors" data-testid="nav-login">Se connecter</Link>
            )}
          </div>
        </div>
      </nav>

      {/* === HERO === */}
      <section className="pt-28 pb-20 px-4 relative overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/8 via-transparent to-[#4ECDC4]/5 pointer-events-none" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#FF6B35]/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-10 w-96 h-96 bg-[#4ECDC4]/4 rounded-full blur-[150px] pointer-events-none" />

        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-16 relative">
          {/* Left: Text */}
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-[#FF6B35] rounded-full animate-pulse" />
              <span className="text-[#FF6B35] text-sm font-semibold tracking-wide">MAGIC · POKÉMON · YU-GI-OH!</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Trouvez des joueurs<br /><span className="text-[#FF6B35]">TCG</span> en un clic
            </h1>
            <p className="text-[#8890a8] text-lg mb-8 max-w-lg mx-auto lg:mx-0">
              Trouvez des joueurs, rejoignez des tournois et gérez vos parties de cartes à collectionner. Magic, Pokémon, Yu-Gi-Oh! et plus encore.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Link to={isAuthenticated ? "/home" : "/login"} className="flex items-center justify-center gap-2 bg-[#FF6B35] hover:bg-[#e55a25] text-white font-semibold px-6 py-3.5 rounded-xl transition-all shadow-lg shadow-[#FF6B35]/20 hover:shadow-[#FF6B35]/30" data-testid="hero-cta-primary">
                {isAuthenticated ? "Accéder à mon espace" : "Se connecter"}
              </Link>
              <a href="#parties" className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold px-6 py-3.5 rounded-xl transition-all" data-testid="hero-cta-secondary">
                Parties près de chez moi
              </a>
            </div>
          </div>

          {/* Right: Phone Mockup */}
          <div className="flex-1 flex justify-center">
            <div className="relative">
              {/* Phone frame */}
              <div className="w-[280px] h-[560px] bg-[#0d1120] rounded-[3rem] border-[3px] border-[#3a4060] shadow-2xl shadow-black/40 relative overflow-hidden">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-7 bg-[#0d1120] rounded-b-2xl z-10 border-b-2 border-x-2 border-[#3a4060]" />
                {/* Screen content */}
                <div className="w-full h-full rounded-[2.8rem] overflow-hidden flex flex-col items-center justify-center bg-[#0d1120] p-6">
                  {/* ArcanApp Logo */}
                  <div className="mb-8 flex-shrink-0">
                    <img src="/game-logos/arcanapp-blue.png" alt="ArcanApp" className="w-48 h-auto object-contain" />
                  </div>
                  {/* Divider line */}
                  <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#FF6B35] to-transparent mb-8" />
                  {/* Game logo carousel */}
                  <div className="relative w-48 h-48 flex items-center justify-center">
                    {GAME_LOGOS.map((logo, i) => (
                      <img
                        key={i}
                        src={logo.src}
                        alt={logo.name}
                        className={`absolute inset-0 w-full h-full object-contain p-2 transition-all duration-700 ease-in-out ${
                          i === currentLogo
                            ? "opacity-100 scale-100"
                            : "opacity-0 scale-90"
                        }`}
                      />
                    ))}
                  </div>
                  {/* Game name */}
                  <p className="text-[#8890a8] text-xs mt-4 font-medium tracking-wide h-4 transition-opacity duration-300">
                    {GAME_LOGOS[currentLogo]?.name}
                  </p>
                  {/* Dots */}
                  <div className="flex gap-1 mt-4">
                    {GAME_LOGOS.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentLogo(i)}
                        className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                          i === currentLogo ? "bg-[#FF6B35] w-4" : "bg-[#3a4060]"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              {/* Glow behind phone */}
              <div className="absolute -inset-8 bg-[#FF6B35]/8 rounded-full blur-[60px] -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* === GAME LOGOS STRIP === */}
      <section className="py-8 border-y border-white/5 bg-[#151a2e]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-center gap-8 flex-wrap opacity-50">
            {GAME_LOGOS.map((logo, i) => (
              <img key={i} src={logo.src} alt={logo.name} className="h-8 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 cursor-pointer" />
            ))}
          </div>
        </div>
      </section>

      {/* === FONCTIONNALITES === */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">Fonctionnalités</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: Trophy, title: "Tournois", desc: "Créez et rejoignez des tournois pour Magic, Pokémon, Yu-Gi-Oh!, Lorcana et plus." },
              { icon: Map, title: "Carte interactive", desc: "Trouvez des joueurs et des événements près de chez vous grâce à la carte interactive." },
              { icon: Users, title: "Communauté", desc: "Messagerie intégrée, groupes automatiques et notifications personnalisées." },
              { icon: Shield, title: "Gestion complète", desc: "Gérez vos parties, acceptez des joueurs et suivez vos statistiques." },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4 bg-[#1e2440] border border-white/5 rounded-2xl p-6 hover:border-[#FF6B35]/20 transition-all group">
                <div className="w-12 h-12 bg-[#FF6B35]/10 group-hover:bg-[#FF6B35]/20 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors">
                  <Icon className="w-6 h-6 text-[#FF6B35]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{title}</h3>
                  <p className="text-[#8890a8] text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === NEWSLETTER === */}
      <section className="py-20 px-4 bg-[#151a2e]" id="newsletter">
        <div className="max-w-lg mx-auto text-center">
          <div className="w-14 h-14 bg-[#FF6B35]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Mail className="w-7 h-7 text-[#FF6B35]" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Restez informé</h2>
          <p className="text-[#8890a8] mb-6">Recevez les dernières nouvelles et mises à jour d'ArcanApp.</p>
          {emailSent ? (
            <div className="bg-[#4CAF50]/10 border border-[#4CAF50]/20 rounded-xl p-4 text-[#4CAF50] font-medium" data-testid="newsletter-success">
              Merci ! Vous êtes inscrit.
            </div>
          ) : (
            <form onSubmit={handleNewsletter} className="flex gap-2" data-testid="newsletter-form">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.com" required className="flex-1 bg-[#1e2440] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#555d78] focus:outline-none focus:border-[#FF6B35] transition-colors" data-testid="newsletter-email" />
              <button type="submit" className="bg-[#FF6B35] hover:bg-[#e55a25] text-white font-semibold px-6 py-3 rounded-xl transition-colors flex items-center gap-2 shadow-lg shadow-[#FF6B35]/20" data-testid="newsletter-submit">
                <Send className="w-4 h-4" /> S'inscrire
              </button>
            </form>
          )}
        </div>
      </section>

      {/* === REJOIGNEZ-NOUS === */}
      <section className="py-20 px-4">
        <div className="max-w-lg mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8">Rejoignez-nous !</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => setContactOpen(true)} className="flex items-center justify-center gap-2 bg-[#FF6B35] hover:bg-[#e55a25] text-white font-semibold px-6 py-3.5 rounded-xl transition-all shadow-lg shadow-[#FF6B35]/20" data-testid="open-contact">
              <Mail className="w-5 h-5" /> Nous contacter
            </button>
            <a href="https://www.instagram.com/arcanapp.tcg/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white font-semibold px-6 py-3.5 rounded-xl transition-opacity hover:opacity-90 shadow-lg shadow-[#833AB4]/15" data-testid="instagram-link">
              <Instagram className="w-5 h-5" /> @arcanapp.tcg
            </a>
          </div>
        </div>
      </section>

      {/* === NEARBY PLAYER GAMES === */}
      <section className="py-16 px-4" id="parties">
        <div className="max-w-3xl mx-auto">
          <NearbyGamesSection isAuthenticated={isAuthenticated} />
        </div>
      </section>

      {/* === FOOTER === */}
      <footer className="py-8 px-4 border-t border-white/5 bg-[#151a2e]">
        <div className="max-w-6xl mx-auto flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-new.png" alt="ArcanApp" className="w-6 h-6 rounded" />
              <span className="text-[#555d78] text-sm">&copy; 2025 ArcanApp. Tous droits réservés.</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/home" className="text-[#555d78] hover:text-white text-sm transition-colors">Home</Link>
              <Link to="/login" className="text-[#555d78] hover:text-white text-sm transition-colors">Connexion</Link>
              <a href="https://www.instagram.com/arcanapp.tcg/" target="_blank" rel="noopener noreferrer" className="text-[#555d78] hover:text-white transition-colors"><Instagram className="w-4 h-4" /></a>
            </div>
          </div>
          <p className="text-[#3d4560] text-xs text-center leading-relaxed" data-testid="disclaimer">
            ArcanApp n'est pas affilié, associé, autorisé, approuvé par, ou de quelque manière que ce soit officiellement lié à Wizards of the Coast (Magic: The Gathering), The Pokémon Company (Pokémon), Konami (Yu-Gi-Oh!), Ravensburger (Lorcana) ou tout autre éditeur de jeux de cartes à collectionner. Tous les noms de produits, logos et marques sont la propriété de leurs détenteurs respectifs.
          </p>
        </div>
      </footer>

      {/* === CONTACT MODAL === */}
      {contactOpen && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setContactOpen(false)}>
          <div className="bg-[#1e2440] border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()} data-testid="contact-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Nous contacter</h3>
              <button onClick={() => setContactOpen(false)} className="p-1 rounded-lg hover:bg-white/5 transition-colors"><X className="w-5 h-5" /></button>
            </div>
            {contactSent ? (
              <div className="bg-[#4CAF50]/10 border border-[#4CAF50]/20 rounded-xl p-4 text-[#4CAF50] font-medium text-center" data-testid="contact-success">
                Message envoyé ! Nous vous répondrons rapidement.
              </div>
            ) : (
              <form onSubmit={handleContact} className="space-y-4">
                <input type="text" value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} placeholder="Votre nom" required className="w-full bg-[#151a2e] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#555d78] focus:outline-none focus:border-[#FF6B35]" data-testid="contact-name" />
                <input type="email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} placeholder="Votre email" required className="w-full bg-[#151a2e] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#555d78] focus:outline-none focus:border-[#FF6B35]" data-testid="contact-email" />
                <textarea value={contactForm.message} onChange={e => setContactForm({ ...contactForm, message: e.target.value })} placeholder="Votre message" required rows={4} className="w-full bg-[#151a2e] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#555d78] focus:outline-none focus:border-[#FF6B35] resize-none" data-testid="contact-message" />
                <button type="submit" className="w-full bg-[#FF6B35] hover:bg-[#e55a25] text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg shadow-[#FF6B35]/20" data-testid="contact-submit">
                  <Send className="w-4 h-4" /> Envoyer
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
